import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Divider,
  Space,
  message,
  Spin
} from 'antd';
import {
  RobotOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePredictCost, useModelAccuracy, usePredictionHistory } from '../../hooks/useEstimation';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { ItemFeatures } from '../../services/estimation.service';

const { Option } = Select;

export const AICostPredictorPage: React.FC = () => {
  const { formatCurrency, baseCurrency } = useCurrency();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [predictionResult, setPredictionResult] = useState<any>(null);

  // Queries
  const predictCostMutation = usePredictCost();
  const { data: accuracyData, isLoading: accuracyLoading } = useModelAccuracy();
  const { data: historyData, isLoading: historyLoading } = usePredictionHistory(10);

  const modelAccuracy = ((accuracyData as { data?: Record<string, unknown> })?.data ??
    (accuracyData as Record<string, unknown>) ??
    {}) as Record<string, any>;
  const predictionHistory = Array.isArray(historyData)
    ? historyData
    : (historyData as { data?: unknown[] })?.data ?? [];

  // Handle prediction
  const handlePredict = async (values: any) => {
    try {
      const features: ItemFeatures = {
        materialType: values.material_type,
        weight: values.weight,
        volume: values.volume,
        complexityScore: values.complexity_score,
        numberOfOperations: values.number_of_operations,
        surfaceArea: values.surface_area,
        toleranceGrade: values.tolerance_grade,
        finishType: values.finish_type
      };

      const result = await predictCostMutation.mutateAsync(features);
      const payload = result as { data?: unknown };
      setPredictionResult(payload?.data ?? result);
      message.success('Cost predicted successfully!');
    } catch (error) {
      message.error('Failed to predict cost');
    }
  };

  // Similar items columns
  const similarItemsColumns = [
    {
      title: 'Item',
      dataIndex: 'itemName',
      key: 'itemName'
    },
    {
      title: 'Actual Cost',
      dataIndex: 'actualCost',
      key: 'actualCost',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED')
    },
    {
      title: 'Similarity',
      dataIndex: 'similarity',
      key: 'similarity',
      render: (value: number) => (
        <Progress
          percent={Math.round(value * 100)}
          size="small"
          status={value > 0.8 ? 'success' : value > 0.6 ? 'normal' : 'exception'}
        />
      )
    }
  ];

  // Feature importance data
  const featureImportanceData = predictionResult?.featureImportance
    ? Object.entries(predictionResult.featureImportance).map(([key, value]) => ({
        feature: key,
        importance: (value as number) * 100
      }))
    : [];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
          <RobotOutlined style={{ marginRight: 8, color: '#52c41a' }} />
          AI Cost Predictor
        </h1>
        <p style={{ color: '#8c8c8c' }}>
          Get instant cost estimates using machine learning based on item features
        </p>
      </div>

      <Row gutter={24}>
        {/* Left Column - Input Form */}
        <Col span={12}>
          <Card title="Item Features" extra={<ThunderboltOutlined style={{ color: '#52c41a' }} />}>
            <Form form={form} layout="vertical" onFinish={handlePredict}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="material_type"
                    label="Material Type"
                    rules={[{ required: true, message: 'Please select material' }]}
                  >
                    <Select placeholder="Select material">
                      <Option value="Steel">Steel</Option>
                      <Option value="Aluminum">Aluminum</Option>
                      <Option value="Plastic">Plastic</Option>
                      <Option value="Copper">Copper</Option>
                      <Option value="Composite">Composite</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="weight"
                    label="Weight (kg)"
                    rules={[{ required: true, message: 'Please enter weight' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.1}
                      placeholder="Enter weight"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="volume" label="Volume (m³)">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.001}
                      placeholder="Enter volume"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="surface_area" label="Surface Area (m²)">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.1}
                      placeholder="Enter surface area"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="complexity_score"
                    label="Complexity Score (1-10)"
                    rules={[{ required: true, message: 'Please rate complexity' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={10}
                      placeholder="1=Simple, 10=Complex"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="number_of_operations"
                    label="Number of Operations"
                    rules={[{ required: true, message: 'Please enter operations count' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      placeholder="Enter operations"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="finish_type" label="Finish Type">
                    <Select placeholder="Select finish">
                      <Option value="Raw">Raw</Option>
                      <Option value="Painted">Painted</Option>
                      <Option value="Powder Coated">Powder Coated</Option>
                      <Option value="Anodized">Anodized</Option>
                      <Option value="Polished">Polished</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="tolerance_grade" label="Tolerance Grade">
                    <Select placeholder="Select tolerance">
                      <Option value="Standard">Standard</Option>
                      <Option value="Precision">Precision</Option>
                      <Option value="High Precision">High Precision</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={predictCostMutation.isPending}
                  icon={<ThunderboltOutlined />}
                >
                  Predict Cost
                </Button>
              </Form.Item>
            </Form>

            {/* Model Accuracy Section */}
            <Divider />
            <div>
              <h4 style={{ marginBottom: 16 }}>Model Performance</h4>
              {accuracyLoading ? (
                <Spin />
              ) : (
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="R² Score"
                      value={modelAccuracy.r2Score || 0}
                      precision={3}
                      valueStyle={{ color: '#3f8600', fontSize: 20 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="RMSE"
                      value={modelAccuracy.rmse || 0}
                      precision={2}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="MAPE"
                      value={modelAccuracy.mape || 0}
                      suffix="%"
                      precision={1}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Prediction Result */}
        <Col span={12}>
          {predictionResult ? (
            <>
              {/* Prediction Result Card */}
              <Card
                title="Prediction Result"
                extra={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                style={{ marginBottom: 16 }}
              >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <h2 style={{ fontSize: 48, fontWeight: 700, color: '#145A54', margin: 0 }}>
                    {formatCurrency(predictionResult.predictedCost, baseCurrency?.currency_code || 'AED')}
                  </h2>
                  <p style={{ color: '#8c8c8c', marginTop: 8 }}>Predicted Cost</p>
                </div>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Confidence"
                        value={predictionResult.confidenceScore * 100}
                        suffix="%"
                        precision={0}
                        valueStyle={{
                          color: predictionResult.confidenceScore > 0.7 ? '#52c41a' : '#faad14'
                        }}
                      />
                      <Progress
                        percent={Math.round(predictionResult.confidenceScore * 100)}
                        showInfo={false}
                        status={predictionResult.confidenceScore > 0.7 ? 'success' : 'normal'}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Model Version"
                        value={predictionResult.modelVersion}
                        valueStyle={{ fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                </Row>

                <div
                  style={{
                    background: '#f0f2f5',
                    padding: 12,
                    borderRadius: 4,
                    marginBottom: 16
                  }}
                >
                  <p style={{ margin: 0, fontSize: 12 }}>
                    <strong>Confidence Range:</strong>
                  </p>
                  <p style={{ margin: 0, fontSize: 14, color: '#145A54' }}>
                    {formatCurrency(
                      predictionResult.confidenceInterval.lower,
                      baseCurrency?.currency_code || 'AED'
                    )}{' '}
                    –{' '}
                    {formatCurrency(
                      predictionResult.confidenceInterval.upper,
                      baseCurrency?.currency_code || 'AED'
                    )}
                  </p>
                </div>

                <div
                  style={{ background: '#e6f7ff', padding: 12, borderRadius: 4, marginBottom: 16 }}
                >
                  <p style={{ margin: 0, fontSize: 12, color: '#0050b3' }}>
                    {predictionResult.recommendation}
                  </p>
                </div>

                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate('/estimation/builder')}
                  >
                    Create Full Estimation
                  </Button>
                  <Button onClick={() => setPredictionResult(null)}>New Prediction</Button>
                </Space>
              </Card>

              {/* Similar Items */}
              {predictionResult.similarItems && predictionResult.similarItems.length > 0 && (
                <Card title="Similar Items" size="small" style={{ marginBottom: 16 }}>
                  <Table
                    columns={similarItemsColumns}
                    dataSource={predictionResult.similarItems}
                    rowKey="itemId"
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}

              {/* Feature Importance */}
              {featureImportanceData.length > 0 && (
                <Card title="Feature Importance" size="small">
                  {featureImportanceData
                    .sort((a, b) => b.importance - a.importance)
                    .map((item) => (
                      <div key={item.feature} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12 }}>{item.feature}</span>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {item.importance.toFixed(1)}%
                          </span>
                        </div>
                        <Progress
                          percent={item.importance}
                          showInfo={false}
                          strokeColor="#145A54"
                          size="small"
                        />
                      </div>
                    ))}
                </Card>
              )}
            </>
          ) : (
            <Card style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: '#8c8c8c' }}>
                <RobotOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                <p>Enter item features and click "Predict Cost" to see results</p>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Prediction History */}
      {predictionHistory.length > 0 && (
        <Card
          title="Recent Predictions"
          style={{ marginTop: 24 }}
          extra={<BarChartOutlined />}
        >
          <Table
            columns={[
              {
                title: 'Date',
                dataIndex: 'predictionDate',
                key: 'predictionDate',
                render: (date: string) => new Date(date).toLocaleDateString()
              },
              {
                title: 'Item',
                dataIndex: 'itemId',
                key: 'itemId',
                render: (id: string) => `Item-${id.substring(0, 8)}`
              },
              {
                title: 'Predicted Cost',
                dataIndex: 'predictedCost',
                key: 'predictedCost',
                render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED')
              },
              {
                title: 'Actual Cost',
                dataIndex: 'actualCost',
                key: 'actualCost',
                render: (value?: number) =>
                  value != null
                    ? formatCurrency(value, baseCurrency?.currency_code || 'AED')
                    : <Tag color="default">Pending</Tag>
              },
              {
                title: 'Accuracy',
                dataIndex: 'predictionAccuracy',
                key: 'predictionAccuracy',
                render: (value?: number) =>
                  value ? (
                    <Tag color={value > 80 ? 'success' : value > 60 ? 'warning' : 'error'}>
                      {value.toFixed(1)}%
                    </Tag>
                  ) : (
                    <Tag color="default">N/A</Tag>
                  )
              }
            ]}
            dataSource={predictionHistory}
            rowKey="predictionId"
            pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] }}
            size="small"
            loading={historyLoading}
          />
        </Card>
      )}
    </div>
  );
};

export default AICostPredictorPage;

